// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { Amulet } from '@daml.js/splice-amulet/lib/Splice/Amulet';
import { CredentialBilling } from '@daml.js/utility-credential-app-v0/lib/Utility/Credential/App/V0/Model/Billing';
import FormDialog from '../../components/Dialog/FormDialog';
import TextInput from '../../components/Form/TextInput';
import { useScanProxyApiClient } from '../../contexts/ScanProxyApiContext';
import useTopUpCredentialBillingLockedDeposit from '../../hooks/mutations/credential/billing/useTopUpCredentialBillingLockedDeposit';
import { useCredentialUserService } from '../../hooks/queries/credential/onboarding/useUserService';
import TemplateContract from '../../utils/TemplateContract';
import { appTransferContextWithDisclosures } from '../../utils/appTransferContextWithDisclosures';
import { ERR_TOPUP_CRED_BILL } from '../../utils/strings';

type DialogProps = {
  open: boolean;
  onClose: () => void;
  row: TemplateContract<CredentialBilling> | undefined;
  amulets: TemplateContract<Amulet>[] | undefined;
};

export const TopUpLockedDepositDialog = ({ open, onClose, row, amulets }: DialogProps) => {
  const [amount, setAmount] = useState<string>('');
  const scanProxyApi = useScanProxyApiClient();
  const userService = useCredentialUserService();
  const topUpCredentialBillingLockedDeposit = useTopUpCredentialBillingLockedDeposit();
  const { showBoundary } = useErrorBoundary<Error>();
  const resetState = useCallback(() => {
    setAmount('');
  }, [setAmount]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleSubmit = useCallback(async () => {
    if (row !== undefined) {
      if (!userService.data) {
        showBoundary(new Error(ERR_TOPUP_CRED_BILL, { cause: 'User service is not available.' }));
        return;
      }

      const { appTransferContext, disclosedContracts } = await appTransferContextWithDisclosures(
        scanProxyApi,
        30,
      );

      await topUpCredentialBillingLockedDeposit({
        cid: userService.data.contractId,
        argument: {
          appTransferContext,
          credentialBillingCid: row.contractId,
          amountUsd: amount,
          coinCids: (amulets ?? []).map((a) => a.contractId),
        },
        meta: {
          disclosedContracts,
        },
      });
    }
    handleClose();
  }, [
    userService,
    row,
    amount,
    amulets,
    scanProxyApi,
    topUpCredentialBillingLockedDeposit,
    handleClose,
    showBoundary,
  ]);

  const disableSubmit = !amount;
  return (
    <FormDialog
      open={open}
      title="TopUp Locked Paid Credential Deposit"
      onCancel={handleClose}
      onSubmit={handleSubmit}
      submitName="Top Up"
      isSubmitDisabled={disableSubmit}
    >
      <TextInput
        disabled
        label="Billing Holder for Top Up"
        value={row?.payload.holder ?? ''}
        setValue={() => {}}
      />
      <TextInput
        type="number"
        label="Amount to Top Up (USD)"
        value={amount}
        setValue={setAmount}
        required
      />
    </FormDialog>
  );
};
